import React from 'react';
import {
  Box,
  Chip,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from '@mui/material';
import { HALL_EVENT_PAYMENT_METHOD_OPTIONS, resolveAdvanceAmountForPaymentType } from '../utils/hallEventForm';

const HALL_ACCENT = '#4ce1b6';

export default function HallEventPaymentFields({
  paymentType,
  paymentMode,
  advanceAmount,
  totalAmount,
  onPaymentTypeChange,
  onPaymentModeChange,
  onAdvanceAmountChange,
  isDark,
  readOnly = false,
  subtleText = '#94a3b8',
}) {
  const toggleSx = {
    width: '100%',
    mb: 1.5,
    gap: 1,
    '& .MuiToggleButtonGroup-grouped': {
      border: 'none !important',
      borderRadius: '14px !important',
      margin: 0,
    },
    '& .MuiToggleButton-root': {
      border: 'none',
      color: isDark ? '#aab2bc' : '#5a646e',
      flex: 1,
      py: 1.15,
      fontSize: '0.82rem',
      fontWeight: 600,
      textTransform: 'none',
      borderRadius: '14px',
      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)',
      '&.Mui-selected': {
        backgroundColor: HALL_ACCENT,
        color: '#031512',
        fontWeight: 800,
        '&:hover': {
          backgroundColor: alpha(HALL_ACCENT, 0.92),
        },
      },
      '&:hover': {
        backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.055)',
      },
    },
  };

  const handlePaymentTypeChange = (_, nextType) => {
    if (!nextType || readOnly) return;
    onPaymentTypeChange(nextType);
  };

  return (
    <Grid item xs={12}>
      <Typography variant="caption" sx={{ color: subtleText, fontWeight: 900, letterSpacing: '0.08em' }}>
        PAYMENT
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={paymentType || 'full'}
        onChange={handlePaymentTypeChange}
        sx={toggleSx}
      >
        <ToggleButton value="full">Full Payment</ToggleButton>
        <ToggleButton value="partial">Partial Payment</ToggleButton>
        <ToggleButton value="due">Due</ToggleButton>
      </ToggleButtonGroup>

      {paymentType !== 'due' ? (
        <TextField
          label={paymentType === 'full' ? 'Payment Amount' : 'Advance Amount'}
          type="number"
          value={
            paymentType === 'full'
              ? String(totalAmount || advanceAmount || '')
              : String(advanceAmount || '')
          }
          onChange={(event) => onAdvanceAmountChange(event.target.value)}
          size="small"
          fullWidth
          required
          disabled={readOnly || paymentType === 'full'}
          sx={{
            mb: 1.5,
            '& .MuiFormLabel-asterisk': {
              color: 'inherit',
            },
          }}
          InputProps={{ startAdornment: <InputAdornment position="start">INR</InputAdornment> }}
        />
      ) : (
        <Box
          sx={{
            mb: 1.5,
            px: 1.5,
            py: 1.25,
            borderRadius: '14px',
            border: 'none',
            backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)',
            color: isDark ? '#aab2bc' : '#5a646e',
            fontSize: '0.82rem',
          }}
        >
          Payment will be collected later. No advance is recorded for this booking.
        </Box>
      )}

      {paymentType !== 'due' ? (
        <>
          <Typography variant="caption" sx={{ color: subtleText, fontWeight: 900, letterSpacing: '0.08em' }}>
            PAYMENT METHOD *
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.8 }}>
            {HALL_EVENT_PAYMENT_METHOD_OPTIONS.map((option) => {
              const selected = paymentMode === option.value;
              return (
                <Chip
                  key={option.value}
                  label={option.label}
                  clickable={!readOnly}
                  onClick={readOnly ? undefined : () => onPaymentModeChange(option.value)}
                  sx={{
                    height: 32,
                    borderRadius: '10px',
                    cursor: readOnly ? 'default' : 'pointer',
                    fontWeight: selected ? 700 : 500,
                    fontSize: '0.75rem',
                    letterSpacing: '0.02em',
                    border: '1.5px solid',
                    borderColor: selected
                      ? HALL_ACCENT
                      : (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'),
                    backgroundColor: selected
                      ? alpha(HALL_ACCENT, isDark ? 0.22 : 0.12)
                      : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'),
                    color: selected ? HALL_ACCENT : (isDark ? '#aab2bc' : '#5a646e'),
                    boxShadow: selected ? `0 0 12px ${alpha(HALL_ACCENT, 0.35)}` : 'none',
                    '&:hover': {
                      backgroundColor: selected
                        ? alpha(HALL_ACCENT, isDark ? 0.3 : 0.18)
                        : alpha(HALL_ACCENT, isDark ? 0.12 : 0.07),
                      borderColor: HALL_ACCENT,
                    },
                  }}
                />
              );
            })}
          </Stack>
        </>
      ) : null}
    </Grid>
  );
}

export function applyHallEventPaymentTypeChange(prevForm, nextPaymentType) {
  return {
    ...prevForm,
    payment_type: nextPaymentType,
    advance_amount: resolveAdvanceAmountForPaymentType(
      nextPaymentType,
      prevForm.amount,
      prevForm.advance_amount
    ),
  };
}

export function syncHallEventAdvanceWithAmount(prevForm) {
  if (prevForm.payment_type !== 'full') {
    return prevForm;
  }
  return {
    ...prevForm,
    advance_amount: resolveAdvanceAmountForPaymentType('full', prevForm.amount, prevForm.advance_amount),
  };
}
